import React, {ReactNode} from 'react';
import {Badge, Dropdown, Icon, MenuListItem, Modal} from '@albert-io/atomic';
import CustomerFeedbackDialogue from 'client/CustomerFeedback/CustomerFeedbackDialogue.react';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import sessionStore from 'client/Session/SessionStore';
import {Link} from 'react-router';
import {ThemeContext} from 'client/components/Theme/ThemeContext';

import useNavItems from '../useNavItems';

interface Props {
  trigger: ReactNode;
}

const DropdownNavItems = ({trigger}: Props) => {
  const {pathname} = window.location;
  const {dropdownNavItems} = useNavItems();

  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const user = getBaseUserQuery(sessionStore.getUserId()).getResource();
  const firstName = user.getFirstName();

  const {
    isDarkMode,
    toggleTheme,
    isFeatureEnabled: isThemeFeatureEnabled,
    isLoading: isThemeLoading
  } = React.useContext(ThemeContext);
  const isAuthenticated = sessionStore.hasValidSession();

  const handleFeedbackModalToggle = () => {
    setShowFeedbackModal((prev) => !prev);
  };

  const shouldShowDarkModeToggle = isAuthenticated && isThemeFeatureEnabled && !isThemeLoading;

  return (
    <>
      <Dropdown
        trigger={trigger}
        position='bottom-end'
        closeOnItemClicked
        wrapperClassName='global-nav-dropdown'
        trayClassName='z-10! mt-x1 text-nowrap'
        trayProps={{
          modifiers: {
            preventOverflow: {
              padding: 8
            }
          }
        }}
      >
        <div className='flex flex-col gap-x1-half p-x1'>
          {dropdownNavItems.map(
            ({action, hardLink = false, icon, key, name, route, showSeparator = false}) => {
              const dataTestId = `global-nav__dropdown-link-${key}`;
              const iconComponent =
                icon === undefined ? null : <Icon icon={['fal', icon]} className='mr-x1' />;

              if (key === 'feedback') {
                return (
                  <MenuListItem
                    key={key}
                    className='nav-submenu_item'
                    onClick={handleFeedbackModalToggle}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                );
              }

              let dropdownItem;

              if (route !== undefined) {
                dropdownItem = hardLink ? (
                  <MenuListItem
                    as='a'
                    key={key}
                    href={route}
                    className='nav-submenu_item'
                    selected={pathname === route}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                ) : (
                  <MenuListItem
                    as={Link}
                    to={route}
                    key={key}
                    className='nav-submenu_item'
                    selected={pathname === route}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                );
              } else {
                dropdownItem = (
                  <MenuListItem
                    onClick={action}
                    key={key}
                    className='nav-submenu_item'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        action?.();
                      }
                    }}
                    data-testid={dataTestId}
                  >
                    {iconComponent}
                    {name}
                  </MenuListItem>
                );
              }

              if (showSeparator) {
                return [
                  dropdownItem,
                  <div className='nav-submenu_border-line' key={`${key}-separator`} />
                ];
              }
              return dropdownItem;
            }
          )}
          {shouldShowDarkModeToggle && (
            <>
              <div className='nav-submenu_border-line' />
              <MenuListItem
                onMenuItemClick={toggleTheme}
                className='nav-submenu_item'
                data-testid='global-nav__dark-mode-toggle'
              >
                <Icon icon={['fal', isDarkMode ? 'sun' : 'moon']} className='mr-x1' />
                <div className='mr-x2'>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</div>
                {!isDarkMode && (
                  <Badge status='special' size='small' roundness='square' className='ml-auto'>
                    BETA
                  </Badge>
                )}
              </MenuListItem>
            </>
          )}
        </div>
      </Dropdown>
      {showFeedbackModal && (
        <Modal
          className='customer-feedback-modal'
          ariaLabel='Customer Feedback Modal'
          handleClose={handleFeedbackModalToggle}
          longModal
        >
          {() => (
            <CustomerFeedbackDialogue
              name={firstName}
              handleClose={handleFeedbackModalToggle}
              inModal
            />
          )}
        </Modal>
      )}
    </>
  );
};

export default DropdownNavItems;
